import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  IdCard, Plus, Search, Smartphone, Tablet, Monitor, LayoutGrid, Clock, CalendarCheck, DollarSign,
  Tag, Hash, History, Ban, RotateCcw, Copy, Lock, Building2, AlertTriangle, Info, CheckCircle2,
  Check, X, AlertCircle, LucideIcon,
} from 'lucide-react';
import config from '../../../configLoader';
import EmployeeViewManager from '../../../managers/EmployeeViewManager';
import AccountCodeManager from '../../../managers/AccountCodeManager';
import LocationsManager from '../../../managers/LocationsManager';
import { RoleInfo } from '../../../interfaces/EmployeeInfoInterface';
import { AccountCode } from '../../../interfaces/AccountCodeInterface';
import { Location } from '../../../interfaces/LocationsInterface';
import { Page } from '../../../interfaces/PagesInterface';
import { useSiteContext } from '../../../SiteContext';
import {
  ToggleSwitch, MultiSelectPicker, ModalShell, StatusPill, ErrorBanner, PickerOption,
} from '../../../components/v2/adminManagement/AdminManagementKit';
import { PageHead, Banner, EmptyState } from '../workOrders/woShared';
import {
  RoleFieldKey, resolveRoleFieldVisibility, blankRoleFlags, buildRoleWritePayload, ROLE_FIELD_KEYS,
} from '../../../util/roleFieldVisibility';
import '../../../components/v2/adminManagement/AdminManagement.css';
import './EmployeeRoles.css';

/**
 * Employee Roles (Setup › Employee Roles, TAS-668). Replaces the v1 RolePage + RoleModal.
 * Design source: docs/featureBriefs/design_handoff_employee_roles (README + app/roles_hifi).
 *
 * Master list on the left, editor on the right. Which switches render is per tenant
 * (util/roleFieldVisibility); a hidden switch is never sent on an update. Create and
 * Duplicate send every switch (POST zeroes unsent flags), with Mobile App on for a blank role.
 * Deactivating a role that still has active employees is blocked here and by the API
 * (400 ROLE_HAS_EMPLOYEES).
 *
 * Root is `.admin-mgmt.er-page`: the Admin Management kit's styles (buttons, switches,
 * pills, modals, pickers) are scoped to .admin-mgmt; page styles are scoped to .er-page.
 */

// ─────────────────────────────────────────────────────────────── field definitions

type AppKey = 'allowMobileApp' | 'kioskUser' | 'allowAdminAppEmployee';

const APPS: Record<AppKey, { title: string; Icon: LucideIcon; cls: string; blocked: string }> = {
  allowMobileApp: { title: 'Mobile App', Icon: Smartphone, cls: 'a-mobile', blocked: 'the mobile app' },
  kioskUser: { title: 'Kiosk App', Icon: Tablet, cls: 'a-kiosk', blocked: 'kiosk clock-in' },
  allowAdminAppEmployee: { title: 'Admin App – Employee', Icon: Monitor, cls: 'a-adminEmp', blocked: 'Admin App – Employee' },
};
const APP_KEYS: AppKey[] = ['allowMobileApp', 'kioskUser', 'allowAdminAppEmployee'];

interface RowDef {
  k: RoleFieldKey;
  title: string;
  desc: string;
  /** Legacy column name shown as a mono tag. */
  tag?: string;
  /** Parent switch: this row is nested and disabled while the parent is off. */
  dep?: RoleFieldKey;
  /** Apps rows reserve a note slot (warning / hint). */
  note?: boolean;
}

interface SectionDef {
  id: string;
  label: string;
  Icon: LucideIcon;
  sub?: string;
  rows: RowDef[];
  mobileOnly?: boolean;
}

const SECTIONS: SectionDef[] = [
  {
    id: 'apps', label: 'Apps', Icon: LayoutGrid, sub: 'Where people in this role can sign in. A role can allow several.', rows: [
      { k: 'allowMobileApp', title: 'Mobile App', desc: 'Can sign in to the Task & Time mobile app', note: true },
      { k: 'kioskUser', title: 'Kiosk App', desc: 'Can clock in on a kiosk tablet with their PIN', note: true },
      { k: 'allowAdminAppEmployee', title: 'Admin App – Employee', desc: 'Office staff: clock in/out, timesheet and time off from the Admin App', note: true },
    ],
  },
  {
    id: 'te', label: 'Time entry', Icon: Clock, sub: 'Ways employees in this role can record time.', rows: [
      { k: 'recordHours', title: 'Daily clock', tag: 'Record Hours', desc: 'Clock in and out for the day' },
      { k: 'recordHoursJob', title: 'Hourly – Job', desc: 'Log hours against a project job' },
      { k: 'recordHoursJobAcctCodeReq', title: 'Cost code required', desc: 'Every job entry must carry a cost code', dep: 'recordHoursJob' },
      { k: 'recordHoursOther', title: 'Hourly – Other', desc: 'Log non-job hours such as shop, training or travel' },
      { k: 'recordHoursServiceOrder', title: 'Service Order', desc: 'Log time against a service order number' },
      { k: 'recordHoursCustomerServiceText', title: 'Customer Service', desc: 'Free-text customer service entry' },
      { k: 'recordHoursCustomerServiceAndWarranty', title: 'Customer Service & Warranty', desc: 'Customer service entry that can be flagged as warranty' },
      { k: 'recordTasks', title: 'Piece tasks', tag: 'Record Tasks', desc: 'Record quantities against piece-rate tasks' },
      { k: 'recordOperationalTask', title: 'Operational tasks', desc: 'Non-production tasks like cleanup and loading' },
      { k: 'recordCustomerServiceWarranty', title: 'Customer service warranty claims', desc: 'File a warranty claim from a service entry' },
    ],
  },
  {
    id: 'mob', label: 'Mobile behaviour', Icon: Smartphone, mobileOnly: true, sub: 'How the mobile app behaves for this role.', rows: [
      { k: 'recordPhoto', title: 'Require photo on punch', desc: 'A selfie is captured with every clock in and out' },
      { k: 'recordLocation', title: 'Record GPS location', desc: 'Location is stamped on every punch' },
      { k: 'useClockHourlyV2', title: 'Clock / Hourly V2 screens', desc: 'Use the redesigned clock and hourly entry screens' },
      { k: 'recordMealHoursOnly', title: 'Meal hours only', desc: 'Hide clock in/out; the employee enters meal hours only' },
      { k: 'allowTimeGap', title: 'Allow time gaps', desc: 'Entries don’t need to be back-to-back across the day' },
    ],
  },
  {
    id: 'to', label: 'Time off', Icon: CalendarCheck, rows: [
      { k: 'allowTimeOffRequests', title: 'Time off requests', desc: 'Can request time off from the app' },
      { k: 'recordSick', title: 'Sick', desc: 'Can record sick time' },
    ],
  },
  {
    id: 'pay', label: 'Pay rules', Icon: DollarSign, rows: [
      { k: 'allowOnCallPay', title: 'On Call pay', desc: 'Eligible for the weekly On Call $ entered at supervisor approval' },
      { k: 'requireProductionPay', title: 'Production pay required', desc: 'Production totals must be entered before a timesheet is approved' },
    ],
  },
];

const CLASSIFICATION_ROWS: RowDef[] = [
  { k: 'office', title: 'Office role', desc: 'Office staff; used to keep office employees separate from field supervisors' },
  { k: 'supervisor', title: 'Can be a supervisor', desc: 'Appears in supervisor pickers' },
];
const AUTO_LOGIN_ROW: RowDef = {
  k: 'admin', title: 'Auto-create Admin App login', desc: 'Creates an Admin App login when an employee is given this role (NPM sync)',
};

const LABEL: Record<string, string> = { name: 'Role name', stateAbbr: 'State', accountCodes: 'Account codes', locations: 'Supervisor locations' };
[...SECTIONS.flatMap((s) => s.rows), ...CLASSIFICATION_ROWS, AUTO_LOGIN_ROW].forEach((r) => { LABEL[r.k] = r.title; });

const STATES = ['CA', 'NV'];

// ─────────────────────────────────────────────────────────────── helpers

const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;
const countOf = (r: RoleInfo | undefined | null) => r?.activeEmployeeCount ?? 0;

/** Tenant chip text, keyed off the build's REACT_APP_CONFIG (no display name in SiteConfig). */
function tenantLabel(): string | null {
  const key = (process.env.REACT_APP_CONFIG || '').toLowerCase();
  if (key.startsWith('2020ph')) return '20/20 Plumbing & Heating · CA';
  if (key.startsWith('2020nv')) return '20/20 Plumbing & Heating · NV';
  if (key.startsWith('npmech')) return 'NP Mechanical';
  if (key.startsWith('wallcraft')) return 'Wallcraft';
  if (key.startsWith('wcdp')) return 'West Coast Drywall & Paint (WCDP)';
  if (key.startsWith('upf')) return 'UPF';
  return null;
}

function fmtStamp(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${date} ${time}`;
}

/** Booleans and arrays always defined, so drafts compare cleanly. */
function normalizeRole(r: RoleInfo): RoleInfo {
  const out = { ...r, accountCodes: [...(r.accountCodes ?? [])], locations: [...(r.locations ?? [])] } as RoleInfo;
  const rec = out as unknown as Record<string, unknown>;
  ROLE_FIELD_KEYS.forEach((k) => { rec[k] = !!rec[k]; });
  out.stateAbbr = r.stateAbbr ?? '';
  return out;
}

const sameSet = (a: number[], b: number[]) =>
  a.length === b.length && [...a].sort((x, y) => x - y).join('|') === [...b].sort((x, y) => x - y).join('|');

function sortRoles(list: RoleInfo[], sort: 'count' | 'name'): RoleInfo[] {
  const l = [...list];
  if (sort === 'name') l.sort((a, b) => a.name.localeCompare(b.name));
  else l.sort((a, b) => (Number(b.active) - Number(a.active)) || (countOf(b) - countOf(a)) || a.name.localeCompare(b.name));
  return l;
}

function errorText(err: unknown, fallback: string): string {
  const e = err as { response?: { status?: number; data?: { message?: unknown; errorMessage?: unknown } }; message?: string };
  if (e?.response?.status === 403) return 'You need the Manage employee roles permission to change roles.';
  const msg = e?.response?.data?.message ?? e?.response?.data?.errorMessage;
  if (typeof msg === 'string' && msg.trim()) return msg;
  if (!e?.response && e?.message && e.message !== 'Network Error') return e.message;
  return fallback;
}

function isRoleHasEmployees(err: unknown): boolean {
  return (err as { response?: { data?: { code?: unknown } } })?.response?.data?.code === 'ROLE_HAS_EMPLOYEES';
}

type ModalState =
  | { kind: 'guard'; after: () => void }
  | { kind: 'deactivate' }
  | { kind: 'add'; srcId: number | null }
  | null;

// ─────────────────────────────────────────────────────────────── page

const EmployeeRolesPage: React.FC<{ readOnly: boolean }> = ({ readOnly }) => {
  const { userRole, setPage } = useSiteContext();
  const visibility = useMemo(() => resolveRoleFieldVisibility(config), []);
  const vis = (k: RoleFieldKey) => visibility[k];
  const visibleApps = APP_KEYS.filter((k) => visibility[k]);

  const [roles, setRoles] = useState<RoleInfo[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [accountCodes, setAccountCodes] = useState<AccountCode[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);

  const [q, setQ] = useState('');
  const [status, setStatus] = useState<'active' | 'inactive' | 'all'>('active');
  const [appFilter, setAppFilter] = useState<'all' | AppKey>('all');
  const [sort, setSort] = useState<'count' | 'name'>('count');

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [draft, setDraft] = useState<RoleInfo | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState>(null);
  const [actionBusy, setActionBusy] = useState(false);

  const nameRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const flashTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(flashTimer.current), []);

  const orig = useMemo(() => {
    const r = roles?.find((x) => x.roleID === selectedId);
    return r ? normalizeRole(r) : null;
  }, [roles, selectedId]);

  // ── load ──────────────────────────────────────────────────────────────────
  const loadRoles = useCallback(async (): Promise<RoleInfo[] | null> => {
    try {
      const list = await EmployeeViewManager.getEmployeeRoles();
      setRoles(list);
      setLoadError(null);
      return list;
    } catch (err) {
      setLoadError(errorText(err, 'Could not load employee roles.'));
      return null;
    }
  }, []);

  useEffect(() => {
    loadRoles();
    AccountCodeManager.getAccountCodes().then(setAccountCodes).catch((e) => console.error('Failed to load account codes', e));
    LocationsManager.getLocations().then((l: Location[]) => setLocations(l ?? [])).catch((e: unknown) => console.error('Failed to load locations', e));
  }, [loadRoles]);

  const selectNow = useCallback((role: RoleInfo | undefined) => {
    setSelectedId(role ? role.roleID : null);
    setDraft(role ? normalizeRole(role) : null);
    setSaveError(null);
    setSavedFlash(false);
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, []);

  // Default selection: the first active role by employee count.
  useEffect(() => {
    if (!roles || selectedId !== null) return;
    const first = sortRoles(roles.filter((r) => r.active), 'count')[0] ?? sortRoles(roles, 'count')[0];
    if (first) selectNow(first);
  }, [roles, selectedId, selectNow]);

  // ── dirty ─────────────────────────────────────────────────────────────────
  const changes = useMemo(() => {
    if (!draft || !orig) return [] as string[];
    const out: string[] = [];
    if (draft.name !== orig.name) out.push('name');
    if (draft.stateAbbr !== orig.stateAbbr) out.push('stateAbbr');
    const d = draft as unknown as Record<string, unknown>;
    const o = orig as unknown as Record<string, unknown>;
    ROLE_FIELD_KEYS.forEach((k) => { if (visibility[k] && !!d[k] !== !!o[k]) out.push(k); });
    if (!sameSet(draft.accountCodes, orig.accountCodes)) out.push('accountCodes');
    if (!sameSet(draft.locations, orig.locations)) out.push('locations');
    return out;
  }, [draft, orig, visibility]);
  const dirty = !readOnly && changes.length > 0;

  const nameError = useCallback((value: string, selfId: number | null): string => {
    const n = value.trim();
    if (!n) return 'Enter a role name.';
    if ((roles ?? []).some((r) => r.roleID !== selfId && (r.name ?? '').trim().toLowerCase() === n.toLowerCase())) {
      return 'Another role already uses this name.';
    }
    return '';
  }, [roles]);
  const nameErr = draft && draft.name !== orig?.name ? nameError(draft.name, draft.roleID) : '';

  const guard = (fn: () => void) => {
    if (!dirty) { fn(); return; }
    setModal({ kind: 'guard', after: fn });
  };

  // ── edits ─────────────────────────────────────────────────────────────────
  const edit = (patch: Partial<RoleInfo>) => {
    if (readOnly) return;
    setDraft((d) => (d ? { ...d, ...patch } : d));
    setSavedFlash(false);
  };
  const setFlag = (k: RoleFieldKey, v: boolean) => edit({ [k]: v } as Partial<RoleInfo>);

  const discard = () => { if (orig) setDraft(normalizeRole(orig)); setSaveError(null); };

  const save = async (): Promise<boolean> => {
    if (!draft || !orig || saving) return false;
    const err = nameError(draft.name, draft.roleID);
    if (err) { nameRef.current?.focus(); return false; }
    setSaving(true);
    setSaveError(null);
    try {
      // `active` comes from the saved role: only Deactivate / Reactivate change it.
      const payload = buildRoleWritePayload({ ...draft, active: orig.active }, visibility, 'update');
      await EmployeeViewManager.editEmployeeRole(draft.roleID, payload);
      const list = await loadRoles();
      const fresh = list?.find((r) => r.roleID === draft.roleID);
      setDraft(normalizeRole(fresh ?? { ...draft, name: payload.name }));
      setSavedFlash(true);
      window.clearTimeout(flashTimer.current);
      flashTimer.current = window.setTimeout(() => setSavedFlash(false), 2400);
      return true;
    } catch (e) {
      setSaveError(errorText(e, 'The server didn’t respond.'));
      return false;
    } finally {
      setSaving(false);
    }
  };

  // ── deactivate / reactivate ───────────────────────────────────────────────
  const setActive = async (active: boolean) => {
    if (!orig || actionBusy) return;
    setActionBusy(true);
    setSaveError(null);
    try {
      // Saved values only: any unsaved edits were resolved by the guard first.
      await EmployeeViewManager.editEmployeeRole(orig.roleID, buildRoleWritePayload({ ...orig, active }, visibility, 'update'));
      const list = await loadRoles();
      const fresh = list?.find((r) => r.roleID === orig.roleID);
      if (fresh) setDraft(normalizeRole(fresh));
      if (!active && status === 'active') setStatus('all');
      setModal(null);
    } catch (e) {
      setModal(null);
      if (isRoleHasEmployees(e)) await loadRoles(); // count changed underneath us: refresh so the button disables
      setSaveError(errorText(e, active ? 'Couldn’t reactivate the role.' : 'Couldn’t deactivate the role.'));
    } finally {
      setActionBusy(false);
    }
  };

  const defaultState = useMemo(() => {
    const counts: Record<string, number> = {};
    (roles ?? []).forEach((r) => { if (r.stateAbbr) counts[r.stateAbbr] = (counts[r.stateAbbr] ?? 0) + 1; });
    return Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? 'CA';
  }, [roles]);

  // ── create ────────────────────────────────────────────────────────────────
  const createRole = async (name: string, srcId: number | null): Promise<string | null> => {
    const src = srcId !== null ? roles?.find((r) => r.roleID === srcId) : undefined;
    const base = src
      ? normalizeRole(src)
      : ({ ...blankRoleFlags(), stateAbbr: defaultState, accountCodes: [], locations: [] } as unknown as RoleInfo);
    try {
      const created = await EmployeeViewManager.addEmployeeRole(
        buildRoleWritePayload({ ...base, name, active: true }, visibility, 'create'),
      );
      const list = await loadRoles();
      const fresh = list?.find((r) => r.roleID === created?.roleID)
        ?? list?.find((r) => r.name.trim().toLowerCase() === name.trim().toLowerCase());
      setModal(null);
      setQ('');
      if (status === 'inactive') setStatus('active');
      if (fresh) selectNow(fresh);
      window.setTimeout(() => nameRef.current?.focus(), 30);
      return null;
    } catch (e) {
      return errorText(e, 'Couldn’t create the role.');
    }
  };


  // ── list ──────────────────────────────────────────────────────────────────
  const all = useMemo(() => roles ?? [], [roles]);
  const nActive = all.filter((r) => r.active).length;
  const listed = useMemo(() => {
    const query = q.trim().toLowerCase();
    let l = all.filter((r) => status === 'all' || (status === 'active') === r.active);
    if (appFilter !== 'all') l = l.filter((r) => !!r[appFilter]);
    if (query) l = l.filter((r) => r.name.toLowerCase().includes(query));
    return sortRoles(l, sort);
  }, [all, q, status, appFilter, sort]);
  const totalActiveEmployees = all.filter((r) => r.active).reduce((s, r) => s + countOf(r), 0);

  const clearFilters = () => { setQ(''); setStatus('all'); setAppFilter('all'); };

  // ── option lists ──────────────────────────────────────────────────────────
  const codeOptions: PickerOption[] = useMemo(() => {
    const selected = new Set([...(draft?.accountCodes ?? [])]);
    return accountCodes
      .filter((c) => c.active !== false || selected.has(c.accountCodeID))
      .map((c) => ({ id: c.accountCodeID, name: `${c.altAccountCodeID || c.accountCodeID} · ${c.name}` }))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
  }, [accountCodes, draft?.accountCodes]);
  const locationOptions: PickerOption[] = useMemo(
    () => locations.map((l) => ({ id: l.locationID, name: l.name })).sort((a, b) => a.name.localeCompare(b.name)),
    [locations],
  );

  // ── render: editor pieces ─────────────────────────────────────────────────
  const count = countOf(orig);

  const noteFor = (k: RoleFieldKey): React.ReactNode => {
    if (!draft || !orig) return null;
    const app = APPS[k as AppKey];
    if (app && orig[k as AppKey] && !draft[k as AppKey] && count > 0) {
      return (
        <span className="er-warn"><AlertTriangle /><span><b>{plural(count, 'employee')}</b> will be blocked from {app.blocked}.</span></span>
      );
    }
    if (k === 'allowAdminAppEmployee' && draft.allowAdminAppEmployee && vis('office') && !draft.office) {
      return (
        <span className="er-hint"><Info /><span>Office staff?</span>
          {!readOnly && <button type="button" className="linkbtn" onClick={() => setFlag('office', true)}>Mark as Office role</button>}
        </span>
      );
    }
    return null;
  };

  const renderRow = (def: RowDef, sectionDisabled: boolean) => {
    if (!draft) return null;
    const on = !!draft[def.k];
    const parentOff = !!def.dep && !draft[def.dep];
    const disabled = readOnly || sectionDisabled || parentOff;
    const desc = parentOff && def.dep ? `Turn on ${LABEL[def.dep]} to use this` : def.desc;
    return (
      <div key={def.k} className={`er-tr${def.note ? ' has-note' : ''}${def.dep ? ' child' : ''}${disabled ? ' is-dis' : ''}`}>
        <div className="er-tr-main">
          <div className="er-tt">{def.title}{def.tag && <span className="er-legacy">{def.tag}</span>}</div>
          <div className="er-td" title={desc}>{desc}</div>
          {def.note && <div className="er-note">{noteFor(def.k)}</div>}
        </div>
        <div className="er-tr-ctl">
          <ToggleSwitch checked={on} disabled={disabled} ariaLabel={def.title} onChange={(v) => setFlag(def.k, v)} />
        </div>
      </div>
    );
  };

  const renderSection = (s: SectionDef) => {
    if (!draft) return null;
    const rows = s.rows.filter((r) => vis(r.k));
    if (!rows.length) return null;
    const secDis = !!s.mobileOnly && vis('allowMobileApp') && !draft.allowMobileApp;
    const allAppsOff = s.id === 'apps' && !visibleApps.some((k) => draft[k]);
    return (
      <section key={s.id} className={`er-sec er-sec-${s.id}${secDis ? ' sec-dis' : ''}`}>
        <div className="er-sec-h">
          <span className="er-sec-ic"><s.Icon /></span>
          <span className="er-sec-t">{s.label}</span>
          {s.mobileOnly && (
            <span className={`er-sec-note${secDis ? ' show' : ''}`}><Info />Only applies to the mobile app — Mobile App is off</span>
          )}
        </div>
        {(s.sub || allAppsOff) && (
          <div className={`er-sec-sub${allAppsOff ? ' warn' : ''}`}>
            {allAppsOff ? <><AlertTriangle />No apps on — employees in this role can’t record time anywhere.</> : s.sub}
          </div>
        )}
        <div className="er-sec-b">{rows.map((r) => renderRow(r, secDis))}</div>
      </section>
    );
  };

  const renderClassification = () => {
    if (!draft) return null;
    const stateOptions = STATES.includes(draft.stateAbbr) || !draft.stateAbbr ? STATES : [...STATES, draft.stateAbbr];
    return (
      <section className="er-sec er-sec-cls">
        <div className="er-sec-h"><span className="er-sec-ic"><Tag /></span><span className="er-sec-t">Classification</span></div>
        <div className="er-sec-b">
          {CLASSIFICATION_ROWS.filter((r) => vis(r.k)).map((r) => renderRow(r, false))}
          <div className="er-tr">
            <div className="er-tr-main"><div className="er-tt">State</div><div className="er-td">Drives meal and overtime rules</div></div>
            <div className="er-tr-ctl">
              <select
                className="select er-state"
                value={draft.stateAbbr}
                disabled={readOnly}
                aria-label="State"
                onChange={(e) => edit({ stateAbbr: e.target.value })}
              >
                {!draft.stateAbbr && <option value="">—</option>}
                {stateOptions.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="er-tr">
            <div className="er-tr-main"><div className="er-tt">Overtime rule group</div><div className="er-td">Set by payroll configuration</div></div>
            <div className="er-tr-ctl">
              <span className="er-ro" title="Read-only"><Lock />
                {draft.overtimeRuleGroupID != null ? `Group #${draft.overtimeRuleGroupID}` : 'Company default'}
              </span>
            </div>
          </div>
          {vis('admin') && renderRow(AUTO_LOGIN_ROW, false)}
        </div>
      </section>
    );
  };

  const renderChips = () => {
    if (!draft) return null;
    return (
      <section className="er-sec er-sec-ac">
        <div className="er-sec-h"><span className="er-sec-ic"><Hash /></span><span className="er-sec-t">Account codes &amp; supervisor locations</span></div>
        <div className="er-sec-b">
          <div className="er-chipgrid">
            <div className="er-chiprow">
              <div className="er-tt">Account codes</div>
              <div className="er-td er-td-wrap">Account codes employees in this role can charge time to</div>
              <MultiSelectPicker
                options={codeOptions}
                values={draft.accountCodes}
                onChange={(ids) => edit({ accountCodes: ids as number[] })}
                placeholder="Search account codes…"
                addPlaceholder="Add…"
                showAvatars={false}
                disabled={readOnly}
              />
            </div>
            <div className="er-chiprow">
              <div className="er-tt">Supervisor locations</div>
              <div className="er-td er-td-wrap">
                Employees in this role can pick supervisors (and those supervisors’ projects) based at these locations in the mobile app, in addition to their own direct supervisor
              </div>
              <MultiSelectPicker
                options={locationOptions}
                values={draft.locations}
                onChange={(ids) => edit({ locations: ids as number[] })}
                placeholder="Search locations…"
                addPlaceholder="Add…"
                showAvatars={false}
                disabled={readOnly}
              />
            </div>
          </div>
        </div>
      </section>
    );
  };

  const renderEditor = () => {
    if (loadError && !roles) {
      return <div className="card er-ed er-ed-empty"><div className="er-ed-pad"><ErrorBanner>{loadError}</ErrorBanner>
        <button type="button" className="btn sm" onClick={() => loadRoles()}><RotateCcw />Try again</button></div></div>;
    }
    if (!roles) return <div className="card er-ed er-ed-empty"><div className="load-note"><span className="am-spin" /></div></div>;
    if (!draft || !orig) {
      return (
        <div className="card er-ed er-ed-empty">
          <EmptyState Icon={IdCard} title={roles.length ? 'No role selected' : 'No roles yet'}>
            {roles.length ? 'Select a role from the list.' : 'Add the first employee role for this company.'}
          </EmptyState>
        </div>
      );
    }
    const deactivateBlocked = count > 0;
    const deactivateTip = `Reassign the ${count === 1 ? 'active employee' : `${count} active employees`} first.`;
    const n = changes.length;
    const stamp = fmtStamp(orig.modifiedAt);
    return (
      <div className="card er-ed">
        <div className="er-ed-head">
          <span className="er-ed-ic"><IdCard /></span>
          <div className="er-ed-tx">
            <h2>{draft.name.trim() || 'Untitled role'}</h2>
            <div className="er-ed-sub">
              <span className="er-pill-sm"><StatusPill variant={orig.active ? 'active' : 'inactive'} /></span>
              <span>{plural(count, 'active employee')}</span>
            </div>
          </div>
          {!readOnly && (
            <div className="er-ed-act">
              {orig.active ? (
                deactivateBlocked ? (
                  <span className="tipwrap er-tip-down" data-tip={deactivateTip}>
                    <button type="button" className="btn sm" disabled><Ban />Deactivate</button>
                  </span>
                ) : (
                  <button type="button" className="btn sm" disabled={actionBusy} onClick={() => guard(() => setModal({ kind: 'deactivate' }))}>
                    <Ban />Deactivate
                  </button>
                )
              ) : (
                <button type="button" className="btn sm" disabled={actionBusy} onClick={() => guard(() => setActive(true))}>
                  <RotateCcw />Reactivate
                </button>
              )}
              <button type="button" className="btn sm" onClick={() => guard(() => setModal({ kind: 'add', srcId: orig.roleID }))}>
                <Copy />Duplicate
              </button>
            </div>
          )}
        </div>

        <div className="er-ed-body" ref={bodyRef}>
          {readOnly && (
            <div className="er-banner">
              <Banner variant="info" Icon={Lock}><b>View only</b> — ask a super admin for <b>Manage employee roles</b>.</Banner>
            </div>
          )}
          {!orig.active && (
            <div className="er-banner">
              <Banner
                variant="warn"
                Icon={Ban}
                action={readOnly ? undefined : (
                  <button type="button" className="btn sm" disabled={actionBusy} onClick={() => guard(() => setActive(true))}>Reactivate</button>
                )}
              >
                This role is inactive. It isn’t offered when assigning roles
                {count > 0 ? `; ${plural(count, 'employee')} still ha${count === 1 ? 's' : 've'} it until reassigned` : ''}.
              </Banner>
            </div>
          )}

          <div className="er-name">
            <label className="er-flab" htmlFor="erName">Role name</label>
            <input
              id="erName"
              ref={nameRef}
              className={`input${nameErr ? ' err' : ''}`}
              value={draft.name}
              maxLength={60}
              disabled={readOnly}
              onChange={(e) => edit({ name: e.target.value })}
            />
            <div className="er-fielderr">{nameErr && <><AlertCircle />{nameErr}</>}</div>
          </div>

          {SECTIONS.map(renderSection)}
          {renderClassification()}
          {renderChips()}
        </div>

        <div className="er-foot">
          {saveError && (
            <div className="er-errbar" role="alert">
              <AlertCircle />
              <div className="grow"><b>Couldn’t save “{draft.name.trim() || orig.name}”.</b> {saveError} {n > 0 && 'Your changes are still here.'}</div>
              {n > 0 && !readOnly && <button type="button" className="btn sm" disabled={saving} onClick={() => save()}>Try again</button>}
              <button type="button" className="er-x" aria-label="Dismiss" onClick={() => setSaveError(null)}><X /></button>
            </div>
          )}
          <div className="er-last">
            <History />
            {stamp
              ? <span>Last changed{orig.modifiedByName ? <> by <b>{orig.modifiedByName}</b></> : ''} · {stamp}</span>
              : <span>No changes recorded yet</span>}
          </div>
          <div className="grow" />
          {!readOnly && (
            <>
              {savedFlash && !n ? (
                <span className="er-status ok"><CheckCircle2 />Saved</span>
              ) : n ? (
                <span className="er-status dirty" title={changes.map((k) => LABEL[k] ?? k).join(', ')}>
                  <span className="er-dirtydot" />{plural(n, 'unsaved change')}
                </span>
              ) : <span className="er-status" />}
              <button type="button" className="btn" disabled={!n || saving} onClick={discard}>Discard</button>
              <button type="button" className="btn primary er-save" disabled={!n || saving} onClick={() => save()}>
                {saving ? <><span className="er-spin" />Saving…</> : <><Check />Save changes</>}
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  // ── render ────────────────────────────────────────────────────────────────
  const tenant = tenantLabel();
  const addButton = readOnly ? (
    <span className="tipwrap er-tip-down" data-tip="Requires the Manage employee roles permission">
      <button type="button" className="btn primary" disabled><Plus />Add role</button>
    </span>
  ) : (
    <button type="button" className="btn primary" disabled={!roles} onClick={() => guard(() => setModal({ kind: 'add', srcId: null }))}>
      <Plus />Add role
    </button>
  );

  return (
    <div className="admin-mgmt er-page">
      <div className="er-head">
        <PageHead
          Icon={IdCard}
          title="Employee Roles"
          sub={(
            <>
              Every field employee has exactly one role. It controls which apps they can use and how they record time.
              Admin App permissions are set separately in{' '}
              {userRole?.admin
                ? <button type="button" className="er-link" onClick={() => setPage(Page.AdminManagement)}>Admin Roles</button>
                : <b>Admin Roles</b>}.
              {tenant && <div><span className="er-tenant"><Building2 />Showing settings configured for <b>{tenant}</b></span></div>}
            </>
          )}
          actions={<div className="ph-actions">{addButton}</div>}
        />
      </div>

      <div className="er-layout">
        <div className="card er-list">
          <div className="er-list-head">
            <div className="searchwrap">
              <Search />
              <input className="input" placeholder="Search roles…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search roles" />
            </div>
            <div className="er-filters">
              <div className="segmented er-seg">
                {([['active', 'Active', nActive], ['inactive', 'Inactive', all.length - nActive], ['all', 'All', all.length]] as const).map(([v, l, c]) => (
                  <button key={v} type="button" className={status === v ? 'on' : ''} onClick={() => setStatus(v)}>
                    {l} <span className="er-n">{c}</span>
                  </button>
                ))}
              </div>
              <select className="select er-sel" value={appFilter} aria-label="Filter by app" onChange={(e) => setAppFilter(e.target.value as 'all' | AppKey)}>
                <option value="all">All apps</option>
                {visibleApps.map((a) => <option key={a} value={a}>{APPS[a].title}</option>)}
              </select>
              <select className="select er-sel" value={sort} aria-label="Sort" onChange={(e) => setSort(e.target.value as 'count' | 'name')}>
                <option value="count">Most employees</option>
                <option value="name">Name A–Z</option>
              </select>
            </div>
          </div>
          <div className="er-cols"><span>Role</span><span>Apps</span><span>Empl.</span></div>
          <div className="er-list-body">
            {!roles ? (
              <div className="load-note">{loadError ?? <span className="am-spin" />}</div>
            ) : listed.length === 0 ? (
              <div className="er-empty">
                <EmptyState Icon={Search} title={q.trim() ? `No roles match “${q.trim()}”` : 'No roles match these filters'}>
                  Try a different name, or clear the search and filters.
                </EmptyState>
                <button type="button" className="btn sm" onClick={clearFilters}><X />Clear search &amp; filters</button>
              </div>
            ) : listed.map((r) => {
              const on = r.roleID === selectedId;
              const name = on && draft ? (draft.name || r.name) : r.name;
              return (
                <div
                  key={r.roleID}
                  className={`er-li${on ? ' on' : ''}${r.active ? '' : ' dim'}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => { if (!on) guard(() => selectNow(r)); }}
                  onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !on) { e.preventDefault(); guard(() => selectNow(r)); } }}
                >
                  <div className="er-li-name">
                    <span className="nm" title={name}>{name}</span>
                    {on && dirty && <span className="er-dirtydot" title="Unsaved changes" />}
                    {vis('office') && r.office && <span className="er-otag">Office</span>}
                    {!r.active && <span className="er-pill-sm"><StatusPill variant="inactive" /></span>}
                  </div>
                  <div className="er-li-apps">
                    {visibleApps.map((a) => {
                      const A = APPS[a];
                      return (
                        <span key={a} className={`er-ab ${A.cls}${r[a] ? '' : ' off'}`} title={r[a] ? A.title : undefined}>
                          <A.Icon />
                        </span>
                      );
                    })}
                  </div>
                  <div className="er-li-cnt mono">{r.activeEmployeeCount ?? '–'}</div>
                </div>
              );
            })}
          </div>
          <div className="er-list-foot">
            {plural(listed.length, 'role')} shown<span className="grow" /><b className="mono">{totalActiveEmployees}</b>&nbsp;active employees
          </div>
        </div>

        {renderEditor()}
      </div>

      {modal?.kind === 'guard' && draft && orig && (
        <ModalShell
          open
          onClose={() => setModal(null)}
          icon={<AlertTriangle />}
          title="Unsaved changes"
          subtitle={orig.name}
          footer={(
            <>
              <button type="button" className="btn" onClick={() => setModal(null)}>Keep editing</button>
              <div className="er-grow" />
              <button type="button" className="btn" onClick={() => { const after = modal.after; setModal(null); discard(); after(); }}>
                Discard changes
              </button>
              <button
                type="button"
                className="btn primary er-btn-w"
                disabled={saving}
                onClick={async () => { const after = modal.after; setModal(null); if (await save()) after(); }}
              >
                <Check />Save &amp; continue
              </button>
            </>
          )}
        >
          <div className="modal-body">
            <div>You have {plural(changes.length, 'unsaved change')} to <b>{orig.name}</b>. Save before leaving?</div>
            <div className="er-chlist">
              {changes.slice(0, 6).map((k) => <span key={k} className="perm-chip">{LABEL[k] ?? k}</span>)}
              {changes.length > 6 && <span className="perm-chip">+{changes.length - 6} more</span>}
            </div>
          </div>
        </ModalShell>
      )}

      {modal?.kind === 'deactivate' && orig && (
        <ModalShell
          open
          onClose={() => { if (!actionBusy) setModal(null); }}
          icon={<Ban />}
          variant="danger"
          title={`Deactivate “${orig.name}”?`}
          footer={(
            <>
              <button type="button" className="btn" disabled={actionBusy} onClick={() => setModal(null)}>Cancel</button>
              <button type="button" className="btn danger-solid er-btn-w" disabled={actionBusy} onClick={() => setActive(false)}>
                {actionBusy ? <span className="er-spin" /> : <Ban />}Deactivate role
              </button>
            </>
          )}
        >
          <div className="modal-body">
            <div className="er-confirm">
              <div className="er-big mono">{count}</div>
              <div><b>No employees have this role.</b><br />It can be reactivated at any time.</div>
            </div>
            <div className="muted er-footnote">An inactive role isn’t offered when assigning roles to new or existing employees.</div>
          </div>
        </ModalShell>
      )}

      {modal?.kind === 'add' && roles && (
        <AddRoleModal
          roles={roles}
          srcId={modal.srcId}
          nameError={(v) => nameError(v, null)}
          onClose={() => setModal(null)}
          onCreate={createRole}
        />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────── Add / Duplicate modal

const AddRoleModal: React.FC<{
  roles: RoleInfo[];
  srcId: number | null;
  nameError: (v: string) => string;
  onClose: () => void;
  onCreate: (name: string, srcId: number | null) => Promise<string | null>;
}> = ({ roles, srcId, nameError, onClose, onCreate }) => {
  const src = srcId !== null ? roles.find((r) => r.roleID === srcId) : undefined;
  const [name, setName] = useState(src ? `${src.name} (copy)`.slice(0, 60) : '');
  const [from, setFrom] = useState<string>(src ? String(src.roleID) : '');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const activeRoles = useMemo(() => sortRoles(roles.filter((r) => r.active || r.roleID === srcId), 'count'), [roles, srcId]);

  useEffect(() => {
    const t = window.setTimeout(() => { inputRef.current?.focus(); inputRef.current?.select(); }, 30);
    return () => window.clearTimeout(t);
  }, []);

  const submit = async () => {
    if (busy) return;
    const e = nameError(name);
    if (e) { setErr(e); inputRef.current?.focus(); return; }
    setBusy(true);
    const serverErr = await onCreate(name.trim(), from ? Number(from) : null);
    setBusy(false);
    if (serverErr) setErr(serverErr);
  };

  return (
    <ModalShell
      open
      onClose={() => { if (!busy) onClose(); }}
      icon={src ? <Copy /> : <Plus />}
      title={src ? 'Duplicate role' : 'Add role'}
      subtitle={src ? `Copies every setting from ${src.name}. Employees aren’t copied.` : 'Set it up in the editor after creating.'}
      footer={(
        <>
          <button type="button" className="btn" disabled={busy} onClick={onClose}>Cancel</button>
          <button type="button" className="btn primary er-btn-w" disabled={busy} onClick={submit}>
            {busy ? <span className="er-spin" /> : <Plus />}Create role
          </button>
        </>
      )}
    >
      <div className="modal-body er-add">
        <div className="frow">
          <label htmlFor="erAddName">Role name</label>
          <input
            id="erAddName"
            ref={inputRef}
            className={`input${err ? ' err' : ''}`}
            value={name}
            maxLength={60}
            placeholder="e.g. HVAC Apprentice"
            onChange={(e) => { setName(e.target.value); setErr(e.target.value.trim() ? nameError(e.target.value) : ''); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
          />
          <div className="er-fielderr">{err && <><AlertCircle />{err}</>}</div>
        </div>
        <div className="frow">
          <label htmlFor="erAddFrom">Start from</label>
          <select id="erAddFrom" className="select" value={from} onChange={(e) => setFrom(e.target.value)}>
            <option value="">Blank role — Mobile App on, everything else off</option>
            <optgroup label="Copy an existing role">
              {activeRoles.map((r) => <option key={r.roleID} value={String(r.roleID)}>{r.name}</option>)}
            </optgroup>
          </select>
          <div className="help">Copies apps, time entry, and all other settings. No employees are moved.</div>
        </div>
      </div>
    </ModalShell>
  );
};

export default EmployeeRolesPage;
